import { atom } from 'jotai';
import type { DashboardFilters } from 'src/types/TApiQueries';
import { formatToLocal } from 'src/utils/common';

/**
 * Canonical store for the dashboard's filter state.
 *
 * One atom owns the truth. Two derived atoms project it into the shapes
 * downstream consumers expect:
 *   - `serverFiltersAtom`     -> existing `DashboardFilters` payload for
 *                                `useSearchQuery`.
 *   - `chatContextFiltersAtom`-> compact, JSON-friendly summary that the
 *                                chat-proxy (`ChatContext.activeFilters`)
 *                                can read so the assistant knows which
 *                                filters are pinning the result set.
 *
 * A descriptor table (`FILTER_DESCRIPTORS`) is the single source of truth
 * for "is this filter user-visible?" and is consumed by
 * `<ActiveFilters />` to render pills. New filters cannot ship without a
 * descriptor entry — that's the whole point.
 */

export interface SchemaSelection {
  id: number;
  name: string;
  fields: Record<string, unknown>;
}

/**
 * "Find duplicates" mode: the dashboard lists exact copies of one asset
 * (same file checksum) instead of running a search. `returnTo` is the filter
 * state the user came from, restored when they leave the mode.
 */
export interface DuplicatesOf {
  id: string;
  name: string;
  returnTo: Omit<DashboardFilterState, 'duplicatesOf'> | null;
}

export interface DashboardFilterState {
  // Server-bound criteria (committed values; debounced text inputs write here
  // after their debounce window).
  keyword: string;
  fileName: string;
  contentType: string;
  customContentInput: string;
  uploadTimeRange: { start?: string; end?: string };
  status: string;
  addonsCriteria: string[];
  schema: SchemaSelection | null;
  derivedOnly: boolean;
  // Content versioning: only assets with a version waiting for release
  pendingReleaseOnly: boolean;
  face: string | null;
  freeFormFilters: Record<string, unknown> | null;
  // When set, every other criterion is ignored (see `DuplicatesOf`).
  duplicatesOf: DuplicatesOf | null;

  // Pagination / sorting (kept here so a single dispatch can reset page on
  // filter change without juggling two stores).
  page: number;
  limitPerPage: number;
  hash: string;
  sortBy: string[];
}

export const DEFAULT_LIMIT_PER_PAGE = 30;

export const INITIAL_STATE: DashboardFilterState = {
  keyword: '',
  fileName: '',
  contentType: '',
  customContentInput: '',
  uploadTimeRange: {},
  status: '',
  addonsCriteria: [],
  schema: null,
  derivedOnly: false,
  pendingReleaseOnly: false,
  face: null,
  freeFormFilters: null,
  duplicatesOf: null,
  page: 1,
  limitPerPage: DEFAULT_LIMIT_PER_PAGE,
  hash: '',
  sortBy: ['upload_time_range_DESC'],
};

export const dashboardFiltersAtom = atom<DashboardFilterState>(INITIAL_STATE);

// ---------------------------------------------------------------------------
// Derived: server payload (`DashboardFilters` shape)
// ---------------------------------------------------------------------------

/**
 * Projects the canonical state into the legacy `DashboardFilters` payload
 * that `useSearchQuery` and `getSearch` expect. The shape is byte-equivalent
 * to what the dashboard's old reducer produced, including the
 * `_filespin_original_asset_id` + `require_schema_id` interaction for the
 * "generated assets only" toggle.
 */
export const serverFiltersAtom = atom<DashboardFilters>((get) => {
  const s = get(dashboardFiltersAtom);

  const payload: DashboardFilters = {
    page: s.page,
    hash: s.hash,
    face: s.face,
    duplicates_of: s.duplicatesOf?.id ?? null,
    limit_per_page: s.limitPerPage,
    keyword: s.keyword,
    extended_result: true,
    sort_by: s.sortBy,
    content_type: s.contentType,
    file_name: s.fileName,
    upload_time_range: s.uploadTimeRange,
    status: s.status,
    custom_content_input: s.customContentInput,
    addons_criteria: s.addonsCriteria,
    filters: s.freeFormFilters as any,
    // Always exclude trashed assets from the dashboard search. The legacy
    // reducer included `trashed: false` in INITIAL_FILTERS and preserved it
    // across dispatches; we keep that contract here so the backend doesn't
    // start surfacing trashed assets on the /assets page.
    ...({ trashed: false } as any),
  };

  // Combine schema-form fields with the synthetic generated-assets marker.
  // Mirrors the inline logic that previously lived in Dashboard/component.tsx.
  const baseFields = s.schema?.fields ?? {};
  const schemaFields: Record<string, unknown> = { ...baseFields };
  if (s.derivedOnly) {
    schemaFields._filespin_original_asset_id = '*';
  }

  const hasSchemaId = s.schema?.id != null;
  const hasFields = Object.keys(schemaFields).length > 0;

  if (hasSchemaId || hasFields) {
    payload.schema_criteria = {
      ...(hasSchemaId ? { data_schema_id: s.schema!.id } : {}),
      fields: schemaFields,
    };
  }

  // When showing generated assets across all schemas, the search must not
  // require a specific schema id.
  if (s.derivedOnly && !hasSchemaId) {
    payload.require_schema_id = false;
  }

  if (s.pendingReleaseOnly) {
    payload.has_pending_version = true;
  }

  // Dev-only invariant: catches accidental shape regressions in
  // `serverFiltersAtom` (e.g. forgetting to map a new state slice into the
  // server payload). The check is silent in production builds.
  if (process.env.NODE_ENV !== 'production') {
    assertServerPayloadShape(payload);
  }

  return payload;
});

const REQUIRED_PAYLOAD_KEYS: ReadonlyArray<keyof DashboardFilters | 'trashed'> = [
  'page',
  'hash',
  'limit_per_page',
  'keyword',
  'extended_result',
  'sort_by',
  'content_type',
  'file_name',
  'upload_time_range',
  'status',
  'addons_criteria',
];

let lastWarnedShapeKey: string | null = null;
function assertServerPayloadShape(payload: any): void {
  for (const key of REQUIRED_PAYLOAD_KEYS) {
    if (!(key in payload)) {
      // Throttle to one warning per missing key to avoid flooding the
      // console; a single warning is enough to flag the regression.
      if (lastWarnedShapeKey !== key) {
        lastWarnedShapeKey = key;
        // eslint-disable-next-line no-console
        console.warn(
          `[dashboardFilters] serverFiltersAtom dropped expected key "${String(
            key,
          )}" — backend search may behave unexpectedly.`,
        );
      }
      return;
    }
  }
}

// ---------------------------------------------------------------------------
// Derived: chat-context view
// ---------------------------------------------------------------------------

/**
 * Compact, JSON-friendly summary of the active filter set for the chat
 * proxy. Only includes keys that are currently active so Claude isn't shown
 * a wall of empty defaults.
 */
export const chatContextFiltersAtom = atom<Record<string, unknown>>((get) => {
  const s = get(dashboardFiltersAtom);
  const out: Record<string, unknown> = {};

  if (s.keyword) out.keyword = s.keyword;
  if (s.fileName) out.fileName = s.fileName;
  if (s.contentType && s.contentType !== '*') {
    out.contentType = s.customContentInput || s.contentType;
  } else if (s.customContentInput) {
    out.contentType = s.customContentInput;
  }
  if (s.uploadTimeRange.start || s.uploadTimeRange.end) {
    out.uploadTimeRange = {
      start: s.uploadTimeRange.start,
      end: s.uploadTimeRange.end,
    };
  }
  if (s.status) out.status = s.status;
  if (s.addonsCriteria.length > 0) out.addonsCriteria = s.addonsCriteria;
  if (s.schema) {
    out.schema = {
      id: s.schema.id,
      name: s.schema.name,
      fields: s.schema.fields,
    };
  }
  if (s.derivedOnly) out.derivedOnly = true;
  if (s.pendingReleaseOnly) out.pendingReleaseOnly = true;
  if (s.face) out.faceSearchActive = true;
  if (s.duplicatesOf) {
    out.duplicatesOf = { assetId: s.duplicatesOf.id, name: s.duplicatesOf.name };
  }
  if (s.freeFormFilters && Object.keys(s.freeFormFilters).length > 0) {
    out.freeFormFilters = s.freeFormFilters;
  }

  return out;
});

// ---------------------------------------------------------------------------
// Write-only action atoms
// ---------------------------------------------------------------------------

export type FilterPatch = Partial<DashboardFilterState>;

// Keys that page or order the current results rather than start a new search.
const NON_SEARCH_KEYS = new Set<keyof DashboardFilterState>(['page', 'hash', 'limitPerPage', 'sortBy', 'duplicatesOf']);

/**
 * Generic patch action. Callers spread their own `page: 1, hash: ''` reset
 * into the patch when changing user-facing filters; pagination-only updates
 * (e.g. "go to page 2") omit it.
 *
 * Any new search or filter leaves "Find duplicates" mode, which ignores every
 * other criterion; paging and sorting stay in it.
 */
export const setFilterAtom = atom(null, (get, set, patch: FilterPatch) => {
  const newSearch = (Object.keys(patch) as Array<keyof DashboardFilterState>).some(
    (key) => !NON_SEARCH_KEYS.has(key),
  );
  set(dashboardFiltersAtom, {
    ...get(dashboardFiltersAtom),
    ...(newSearch ? { duplicatesOf: null } : {}),
    ...patch,
  });
});

/**
 * Reset every user-facing filter while preserving pagination preferences
 * (items-per-page, sort) and tearing down face-search (which uses a
 * different backend endpoint).
 */
export const clearAllFiltersAtom = atom(null, (get, set) => {
  const cur = get(dashboardFiltersAtom);
  set(dashboardFiltersAtom, {
    ...INITIAL_STATE,
    limitPerPage: cur.limitPerPage,
    sortBy: cur.sortBy,
  });
});

/**
 * Enter "Find duplicates" mode for one asset. The current filters are kept
 * in `returnTo` so leaving the mode puts the user back on the search they
 * came from; finding duplicates of another asset while already in the mode
 * keeps the original `returnTo`.
 */
export const findDuplicatesAtom = atom(
  null,
  (get, set, asset: { id: string; name: string }) => {
    const cur = get(dashboardFiltersAtom);
    const { duplicatesOf, ...rest } = cur;
    set(dashboardFiltersAtom, {
      ...INITIAL_STATE,
      limitPerPage: cur.limitPerPage,
      sortBy: cur.sortBy,
      duplicatesOf: {
        id: asset.id,
        name: asset.name,
        returnTo: duplicatesOf ? duplicatesOf.returnTo : rest,
      },
    });
  },
);

/** The state that leaving "Find duplicates" mode returns to. */
const exitDuplicatesState = (s: DashboardFilterState): DashboardFilterState => ({
  ...(s.duplicatesOf?.returnTo ?? { ...INITIAL_STATE, limitPerPage: s.limitPerPage, sortBy: s.sortBy }),
  duplicatesOf: null,
  page: 1,
  hash: '',
});


/**
 * Payload for `applyAiSearchIntentAtom`. Mirrors `AiSearchIntent` in
 * `src/shared/chat.ts` plus an optional `schemaLookup` so we can resolve
 * the human-readable schema name (`name.en`) without making the chat
 * subsystem own the schemas query.
 */
export interface AiSearchIntentPayload {
  keyword?: string;
  content_type?: string;
  file_name?: string;
  status?: string;
  upload_time_range?: { start?: string; end?: string };
  sort_by?: string[];
  schema_criteria?: {
    data_schema_id?: number;
    fields?: Record<string, unknown>;
    field?: Record<string, unknown>;
  };
  schemaLookup?: Record<number, { id: number; name: string } | undefined>;
}

/**
 * Merge a search intent emitted by the chat-proxy (or the chat sidebar's
 * "View" button) into the canonical state. Always resets pagination so the
 * user lands on page 1 of the new result set.
 */
export const applyAiSearchIntentAtom = atom(
  null,
  (get, set, intent: AiSearchIntentPayload) => {
    const cur = get(dashboardFiltersAtom);
    // A new search always leaves "Find duplicates" mode.
    const patch: Partial<DashboardFilterState> = { page: 1, hash: '', duplicatesOf: null };

    if (intent.keyword !== undefined) patch.keyword = intent.keyword;
    if (intent.content_type !== undefined) patch.contentType = intent.content_type;
    if (intent.file_name !== undefined) patch.fileName = intent.file_name;
    if (intent.status !== undefined) patch.status = intent.status;
    if (intent.upload_time_range !== undefined) patch.uploadTimeRange = intent.upload_time_range;
    if (intent.sort_by !== undefined) patch.sortBy = intent.sort_by;

    const sc = intent.schema_criteria;
    if (sc && sc.data_schema_id != null) {
      const id = Number(sc.data_schema_id);
      const fields = (sc.fields as Record<string, unknown> | undefined) ?? {};
      const lookup = intent.schemaLookup?.[id];
      patch.schema = {
        id,
        name: lookup?.name ?? `Schema ${id}`,
        fields,
      };
    }

    set(dashboardFiltersAtom, { ...cur, ...patch });
  },
);

// ---------------------------------------------------------------------------
// Descriptor table — single source of truth for active-filter pills
// ---------------------------------------------------------------------------

export type FilterKey =
  | 'keyword'
  | 'file_name'
  | 'content_type'
  | 'custom_content_input'
  | 'upload_time_range'
  | 'status'
  | 'addons_criteria'
  | 'schema'
  | 'schema_field'
  | 'derived_only'
  | 'pending_release_only'
  | 'face'
  | 'duplicates_of'
  | 'free_form_filters';

export interface FilterPill {
  id: string;
  label: string;
  onClear: () => void;
  // Shows this asset's thumbnail in the pill.
  thumbnailAssetId?: string;
}

export interface FilterDescriptor {
  key: FilterKey;
  isActive: (state: DashboardFilterState) => boolean;
  toPills: (
    state: DashboardFilterState,
    apply: (patch: FilterPatch) => void,
  ) => FilterPill[];
}

const ADDON_LABELS: Record<string, string> = {
  ON_DEMAND_IMAGE: 'On-demand Image (ODI)',
  STORYBOARD: 'Video Storyboard',
  VIDEO_TRANSCODE: 'Video Transcode',
  FACE_RECOGNITION: 'Face Recognition',
  IMAGE_ANALYSIS: 'Image Auto-tags',
};

const formatRange = (start?: string, end?: string): string => {
  if (start && end) return `Range: ${formatToLocal(start)} to ${formatToLocal(end)}`;
  if (start) return `Start: ${formatToLocal(start)}`;
  return `End: ${formatToLocal(end)}`;
};

const formatFieldValue = (value: unknown): string => {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(formatFieldValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const resetPaging = { page: 1, hash: '' } as const;

export const FILTER_DESCRIPTORS: FilterDescriptor[] = [
  {
    key: 'keyword',
    isActive: (s) => s.keyword.length > 0,
    toPills: (s, apply) => [
      {
        id: 'keyword',
        label: s.keyword,
        onClear: () => apply({ keyword: '', ...resetPaging }),
      },
    ],
  },
  {
    key: 'file_name',
    isActive: (s) => s.fileName.length > 0,
    toPills: (s, apply) => [
      {
        id: 'fileName',
        label: `File: ${s.fileName}`,
        onClear: () => apply({ fileName: '', ...resetPaging }),
      },
    ],
  },
  {
    key: 'content_type',
    isActive: (s) =>
      s.contentType !== '' && s.contentType !== '*' && s.customContentInput === '',
    toPills: (s, apply) => [
      {
        id: 'contentType',
        label: `Content Type: ${s.contentType}`,
        onClear: () =>
          apply({ contentType: '', customContentInput: '', ...resetPaging }),
      },
    ],
  },
  {
    key: 'custom_content_input',
    isActive: (s) => s.customContentInput !== '',
    toPills: (s, apply) => [
      {
        id: 'customContent',
        label: `Content Type: ${s.customContentInput}`,
        // Mirror the legacy clear behaviour: reset content_type to '*' so the
        // "Custom" radio stays available, and blank the input.
        onClear: () =>
          apply({ contentType: '*', customContentInput: '', ...resetPaging }),
      },
    ],
  },
  {
    key: 'upload_time_range',
    isActive: (s) => Boolean(s.uploadTimeRange.start || s.uploadTimeRange.end),
    toPills: (s, apply) => [
      {
        id: 'uploadTimeRange',
        label: formatRange(s.uploadTimeRange.start, s.uploadTimeRange.end),
        onClear: () => apply({ uploadTimeRange: {}, ...resetPaging }),
      },
    ],
  },
  {
    key: 'status',
    isActive: (s) => s.status !== '',
    toPills: (s, apply) => [
      {
        id: 'status',
        label: `Status: ${s.status}`,
        onClear: () => apply({ status: '', ...resetPaging }),
      },
    ],
  },
  {
    key: 'addons_criteria',
    isActive: (s) => s.addonsCriteria.length > 0,
    toPills: (s, apply) =>
      s.addonsCriteria.map((addon) => ({
        id: `addon:${addon}`,
        label: `Addon: ${ADDON_LABELS[addon] || addon}`,
        onClear: () =>
          apply({
            addonsCriteria: s.addonsCriteria.filter((a) => a !== addon),
            ...resetPaging,
          }),
      })),
  },
  {
    key: 'schema',
    isActive: (s) => s.schema?.id != null,
    toPills: (s, apply) =>
      s.schema?.id == null
        ? []
        : [
            {
              id: 'schema',
              label: `Schema: ${s.schema.name} (ID: ${s.schema.id})`,
              // Clearing the schema also clears any per-field criteria the
              // user had entered against it.
              onClear: () => apply({ schema: null, ...resetPaging }),
            },
          ],
  },
  {
    key: 'schema_field',
    isActive: (s) =>
      s.schema != null && Object.keys(s.schema.fields ?? {}).length > 0,
    toPills: (s, apply) => {
      if (!s.schema) return [];
      const schemaName = s.schema.name;
      return Object.entries(s.schema.fields).map(([fieldKey, value]) => ({
        id: `schemaField:${fieldKey}`,
        label: `${schemaName} · ${fieldKey}: ${formatFieldValue(value)}`,
        onClear: () => {
          if (!s.schema) return;
          const next = { ...s.schema.fields };
          delete next[fieldKey];
          apply({
            schema: { ...s.schema, fields: next },
            ...resetPaging,
          });
        },
      }));
    },
  },
  {
    key: 'derived_only',
    isActive: (s) => s.derivedOnly,
    toPills: (_s, apply) => [
      {
        id: 'derivedOnly',
        label: 'Generated assets only',
        onClear: () => apply({ derivedOnly: false, ...resetPaging }),
      },
    ],
  },
  {
    key: 'pending_release_only',
    isActive: (s) => s.pendingReleaseOnly,
    toPills: (_s, apply) => [
      {
        id: 'pendingReleaseOnly',
        label: 'Awaiting release',
        onClear: () => apply({ pendingReleaseOnly: false, ...resetPaging }),
      },
    ],
  },
  {
    key: 'face',
    isActive: (s) => Boolean(s.face),
    toPills: (_s, apply) => [
      {
        id: 'face',
        label: 'Face search active',
        onClear: () =>
          apply({ face: null, freeFormFilters: null, ...resetPaging }),
      },
    ],
  },
  {
    key: 'duplicates_of',
    isActive: (s) => s.duplicatesOf != null,
    toPills: (s, apply) =>
      s.duplicatesOf == null
        ? []
        : [
            {
              id: 'duplicatesOf',
              label: `Duplicates of ${s.duplicatesOf.name || 'this file'}`,
              thumbnailAssetId: s.duplicatesOf.id,
              onClear: () => apply(exitDuplicatesState(s)),
            },
          ],
  },
  {
    key: 'free_form_filters',
    isActive: (s) =>
      s.freeFormFilters != null && Object.keys(s.freeFormFilters).length > 0,
    toPills: (s, apply) => {
      const ff = s.freeFormFilters || {};
      return Object.entries(ff).map(([k, v]) => ({
        id: `freeForm:${k}`,
        label: `${k}: ${formatFieldValue(v)}`,
        onClear: () => {
          const next = { ...ff };
          delete next[k];
          apply({
            freeFormFilters: Object.keys(next).length > 0 ? next : null,
            ...resetPaging,
          });
        },
      }));
    },
  },
];

/**
 * `true` if any user-facing filter is currently set. Used by the dashboard
 * to decide whether to show the "Clear all" button.
 */
export const anyFilterActiveAtom = atom<boolean>((get) => {
  const s = get(dashboardFiltersAtom);
  return FILTER_DESCRIPTORS.some((d) => d.isActive(s));
});
